import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Payor } from '@finpay-estimation/estimation-types';
import { estimateService } from '../../../../services/estimate-service';
import { showErrorStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { AppDispatch } from '../../../../../shared/state/store';

export function useCurrentInsurancePayors(
  clientId: number | undefined | null,
  patientId: number | undefined | null = null,
): { payors: Payor[]; isLoading: boolean } {
  const dispatch = useDispatch<AppDispatch>();
  const [payors, setPayors] = useState<Payor[]>([]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const cid =
    clientId != null && Number(clientId) > 0 ? Number(clientId) : 0;
  const pidNorm =
    patientId != null && Number(patientId) > 0 ? Number(patientId) : 0;
  const loadKey =
    cid > 0 ? (pidNorm > 0 ? `${pidNorm}:${cid}` : `0:${cid}`) : '';

  const isLoading = cid > 0 && (inFlight || loadedKey !== loadKey);

  useEffect(() => {
    if (!cid) {
      setPayors([]);
      setLoadedKey(null);
      setInFlight(false);
      return;
    }

    const ac = new AbortController();
    setPayors([]);
    setLoadedKey(null);
    setInFlight(true);

    (async () => {
      try {
        const res = await estimateService.getCurrentInsurancePayors(cid, ac.signal);
        if (ac.signal.aborted) {
          return;
        }
        if (res.hasErrors) {
          dispatch(
            showErrorStatus(
              res.errorMessage || 'Failed to load payors for this client',
            ),
          );
          setPayors([]);
          setLoadedKey(loadKey);
          return;
        }
        setPayors(res.entity ?? []);
        setLoadedKey(loadKey);
      } catch (e: unknown) {
        if (ac.signal.aborted) {
          return;
        }
        dispatch(
          showErrorStatus(
            e instanceof Error ? e.message : 'Failed to load payors',
          ),
        );
        setPayors([]);
        setLoadedKey(loadKey);
      } finally {
        if (!ac.signal.aborted) {
          setInFlight(false);
        }
      }
    })();

    return () => {
      ac.abort();
    };
  }, [cid, loadKey, dispatch]);

  return { payors, isLoading };
}
